import type { ComponentType } from "react";

import * as ReactWidgets from "@fluid-app/react-widgets";

import type { PropInfo } from "./utils/createWebComponent";
// Import the generated manifest
import widgetManifest from "../generated/widget-manifest.js";
import { createWebComponent } from "./utils/createWebComponent";

// Define the structure of the manifest entries
interface WidgetInfo {
  dirName: string;
  componentName: string;
  tagName: string;
  propsJsPath: string;
}

// Define a custom window interface with Fluid SDK properties
interface FluidWindow extends Window {
  fluidCart?: {
    open: () => void;
    close: () => void;
    toggle: () => void;
  };
}
// Type-safe access to the window object
const fluidWindow = window as FluidWindow;

/**
 * Register all web components based on the generated manifest
 */
export async function registerWebComponents(): Promise<void> {
  if (typeof window === "undefined" || typeof customElements === "undefined") {
    console.warn(
      "Web components registration failed: not running in a browser environment",
    );
    return;
  }

  try {
    for (const widget of widgetManifest as WidgetInfo[]) {
      // Check if component is already registered
      if (customElements.get(widget.tagName)) {
        continue;
      }

      // Dynamically access the potential React component from the imported module
      // Cast ReactWidgets to an indexable type to allow string access
      const PotentialComponent = (ReactWidgets as Record<string, unknown>)[
        widget.componentName
      ];

      // Check if the accessed property is a function (React components are functions/classes)
      if (typeof PotentialComponent !== "function") {
        console.warn(
          `Could not find a valid React component "${widget.componentName}" in @fluid-app/react-widgets exports, or it was not a function/class.`,
        );
        continue; // Skip to the next widget
      }

      // Now we know PotentialComponent is a function, treat it as the component
      const Component = PotentialComponent as ComponentType<unknown>;

      // Load prop information
      const { propNames, propInfo } = await loadPropInfo(widget);

      // Create and define the web component
      // Use detailed prop info if available, otherwise fall back to prop names
      const propsForComponent = propInfo.length > 0 ? propInfo : propNames;
      const WebComponentElement = createWebComponent(
        Component,
        propsForComponent,
        widget.tagName,
      );

      try {
        // Try to define the custom element with error handling for each element
        customElements.define(widget.tagName, WebComponentElement);
      } catch (defineError) {
        // Check if the error is because the element is already defined
        if (
          defineError instanceof Error &&
          defineError.message.includes("already been defined")
        ) {
          // This is not a critical error, just log it and continue
          console.log(
            `Custom element "${widget.tagName}" already defined. Skipping registration.`,
          );
        } else {
          // For other errors, log warning but don't fail the entire registration process
          console.error(
            `Error defining custom element "${widget.tagName}":`,
            defineError,
          );
        }
      }
    }
  } catch (error) {
    console.error(
      "Failed to register Fluid web components dynamically:",
      error instanceof Error ? error.message : String(error),
    );
  }
}

/**
 * Load prop information for a widget
 */
async function loadPropInfo(
  widget: WidgetInfo,
): Promise<{ propNames: string[]; propInfo: PropInfo[] }> {
  // Default empty arrays for fallback
  let propNames: string[] = [];
  let propInfo: PropInfo[] = [];

  try {
    // First try loading props from within the same bundle
    try {
      try {
        // Try direct import WITHOUT webpack ignore first - this will work if bundled properly
        const relativeImport = (await import(widget.propsJsPath)) as {
          default: string[];
          propInfo?: PropInfo[];
        };

        if (Array.isArray(relativeImport.default)) {
          propNames = relativeImport.default;
          if (
            relativeImport.propInfo &&
            Array.isArray(relativeImport.propInfo)
          ) {
            propInfo = relativeImport.propInfo;
          }
          return { propNames, propInfo };
        }
      } catch {
        // Try again WITH webpack ignore as a fallback - this is needed for some bundlers
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - Dynamic import with webpackIgnore
          const webpackIgnoreImport = (await import(
            /* webpackIgnore: true */
            /* @vite-ignore */
            widget.propsJsPath
          )) as {
            default: string[];
            propInfo?: PropInfo[];
          };

          if (Array.isArray(webpackIgnoreImport.default)) {
            propNames = webpackIgnoreImport.default;
            if (
              webpackIgnoreImport.propInfo &&
              Array.isArray(webpackIgnoreImport.propInfo)
            ) {
              propInfo = webpackIgnoreImport.propInfo;
            }
            return { propNames, propInfo };
          }
        } catch {
          // Continue to next approach
        }
      }
    } catch {
      // Continue to next approach
    }

    // Get the script element to determine the base URL
    const scriptElement = (document.currentScript ??
      document.querySelector(
        'script[id="fluid-cdn-script"]',
      )) as HTMLScriptElement | null;

    // Create an array of potential URLs to try
    const importUrls: string[] = [];

    // Start with the direct path from manifest
    const jsUrl = widget.propsJsPath;

    // First try paths based on the current script location
    if (scriptElement?.src) {
      try {
        // Create a base URL from the script src
        const scriptUrl = new URL(scriptElement.src);

        // Try several variants of the base path
        const urlBase = scriptUrl.href.substring(
          0,
          scriptUrl.href.lastIndexOf("/"),
        );

        // Add relative paths to the script location
        const possibleBases = [
          urlBase, // Same directory as script
          urlBase.substring(0, urlBase.lastIndexOf("/")), // One level up
          `${urlBase}/generated`, // Generated subdirectory
          `${urlBase}/../generated`, // Generated in parent dir
          new URL(".", scriptUrl.href).href, // Using URL API relative path
          new URL("./generated", scriptUrl.href).href, // Using URL API with subdir
        ];

        // For each base, add several variants of the prop path
        possibleBases.forEach((base) => {
          importUrls.push(
            `${base}/${widget.dirName}-props.js`,
            `${base}/generated/${widget.dirName}-props.js`,
            `${base}/${widget.propsJsPath.replace("./generated/", "")}`,
          );
        });
      } catch {
        // Continue with other approaches
      }
    }

    // Add paths for local development with file:// protocol
    if (window.location.protocol === "file:") {
      // For local file path, we need to try various relative paths
      const possiblePaths = [
        // Absolute paths (from project root)
        `/packages/web-widgets/dist/cdn/web-widgets/generated/${widget.dirName}-props.js`,
        `/packages/web-widgets/src/generated/${widget.dirName}-props.js`,
        `/dist/cdn/web-widgets/generated/${widget.dirName}-props.js`,
        `/src/generated/${widget.dirName}-props.js`,

        // Relative paths (from current HTML location)
        `./packages/web-widgets/dist/cdn/web-widgets/generated/${widget.dirName}-props.js`,
        `./packages/web-widgets/src/generated/${widget.dirName}-props.js`,
        `./dist/cdn/web-widgets/generated/${widget.dirName}-props.js`,
        `./src/generated/${widget.dirName}-props.js`,
        `./generated/${widget.dirName}-props.js`,
        `./${widget.dirName}-props.js`,
      ];

      importUrls.push(...possiblePaths);
    }

    // Always add the original path and variants as fallbacks
    if (!importUrls.includes(jsUrl)) {
      importUrls.push(jsUrl);
    }

    // Add URL paths starting with / for web server root
    importUrls.push(
      `/web-widgets/generated/${widget.dirName}-props.js`,
      `/generated/${widget.dirName}-props.js`,
      `/${widget.dirName}-props.js`,
    );

    // Add absolute URL versions for CDN
    if (window.location.origin) {
      importUrls.push(
        `${window.location.origin}/web-widgets/generated/${widget.dirName}-props.js`,
        `${window.location.origin}/generated/${widget.dirName}-props.js`,
        `${window.location.origin}/${widget.dirName}-props.js`,
      );
    }

    // Remove any duplicates
    const uniqueUrls = [...new Set(importUrls)];

    // Try each URL in sequence until one works
    let loadedProps = false;

    // Attempt to load manually by creating a script tag first to preload
    const preloadTag = document.createElement("link");
    preloadTag.rel = "preload";
    preloadTag.as = "script";
    preloadTag.href = `./generated/${widget.dirName}-props.js`;
    document.head.appendChild(preloadTag);

    for (const url of uniqueUrls) {
      try {
        // Try with fetch API first - this bypasses webpack and might work better
        try {
          const response = await fetch(url);

          if (response.ok) {
            await response.text(); // Just to complete the fetch, content not used directly
          }
        } catch {
          // Continue if fetch fails
        }

        // Use type assertion to handle the dynamic import with a known structure
        const propsModule = (await import(
          /* webpackIgnore: true */ /* @vite-ignore */ url
        )) as {
          default: string[];
          propInfo?: PropInfo[];
        };

        // Check if the default export is an array before assigning
        const defaultExport = propsModule.default;
        if (Array.isArray(defaultExport)) {
          propNames = defaultExport;

          // Try to get detailed prop info if available
          if (propsModule.propInfo && Array.isArray(propsModule.propInfo)) {
            propInfo = propsModule.propInfo;
          }

          loadedProps = true;
          break; // Exit the loop when successful
        }
      } catch {
        // Continue to the next URL
      }
    }

    // Attempt to access props directly from window object as a last resort
    if (!loadedProps) {
      try {
        const propKey = `fluid${widget.componentName}Props`;

        // Use a type-safe interface for window props
        interface FluidWindowProp {
          propNames?: string[];
          propInfo?: PropInfo[];
        }

        // Create a more type-safe reference to the window object with potential fluid props
        const windowWithProps = window as unknown as Record<
          string,
          string[] | FluidWindowProp
        >;
        const windowProps = windowWithProps[propKey];

        if (windowProps) {
          // If it's a direct array of strings
          if (Array.isArray(windowProps)) {
            propNames = [...windowProps]; // Create a copy to ensure type safety
          }
          // If it's an object with propNames property
          else {
            // No type assertion needed since TypeScript can infer correctly
            if (windowProps.propNames && Array.isArray(windowProps.propNames)) {
              propNames = [...windowProps.propNames];

              if (windowProps.propInfo && Array.isArray(windowProps.propInfo)) {
                propInfo = [...windowProps.propInfo];
              }
            }
          }
        }
      } catch {
        // Continue with empty props if window access fails
      }
    }

    return { propNames, propInfo };
  } catch (e) {
    console.warn(
      `Warning: Could not load props JSON for ${widget.tagName}. Using empty props. Error: ${e instanceof Error ? e.message : String(e)}`,
    );
    // Use empty arrays for registration if import failed
    propNames = [];
    propInfo = [];
    return { propNames, propInfo };
  }
}

// Auto-register web components if in browser environment
if (typeof window !== "undefined") {
  registerWebComponents()
    .then(() => {
      if (customElements.get("fluid-cart-widget")) {
        // Destructure directly, relying on subsequent type checks
        const { openCart, closeCart, toggleCart } = ReactWidgets;

        // Ensure the specific functions exist and are functions before assigning
        if (
          typeof openCart === "function" &&
          typeof closeCart === "function" &&
          typeof toggleCart === "function"
        ) {
          // Assign with explicit casting after check
          fluidWindow.fluidCart = {
            open: openCart as () => void,
            close: closeCart as () => void,
            toggle: toggleCart as () => void,
          };
        } else {
          console.warn(
            "Cart control functions (openCart, closeCart, toggleCart) not found or not functions in react-widgets exports.",
          );
        }

        // Add click listener for data-fluid-cart attribute
        document.addEventListener("click", (e) => {
          // Use optional chaining for potentially undefined fluidCart or methods
          const cartApi = fluidWindow.fluidCart;
          if (!cartApi) return;

          const target = e.target as HTMLElement;
          const button = target.closest("[data-fluid-cart]");
          if (!button) return;

          const action = button.getAttribute("data-fluid-cart");

          if (action === "open" && typeof cartApi.open === "function")
            cartApi.open();
          else if (action === "close" && typeof cartApi.close === "function")
            cartApi.close();
          else if (action === "toggle" && typeof cartApi.toggle === "function")
            cartApi.toggle();
        });
      }
    })
    .catch((err) => {
      console.error("Error during async web component registration:", err);
    });
}

// Export types for documentation (This part becomes less useful as components are dynamic)
// Consider generating a separate types file or improving documentation.
// export type { BannerWidgetProps, CartWidgetProps }; // Commenting out for now

// export type { BannerWidgetProps, CartWidgetProps }; // Commenting out for now
